<template>
  <section id="auth-service" class="section section-observed self-service-section">
    <div class="service-tabs service-tabs-sticky" role="tablist" aria-label="学会服务导航">
      <button
        v-for="tab in front.serviceTabs"
        :key="tab.value"
        type="button"
        :class="{ active: front.activeServiceTab === tab.value }"
        @click="front.goServicesTab(tab.value)"
      >
        <span class="service-tab-ico" aria-hidden="true" v-html="tabIcon(tab.icon)" />
        <span>{{ tab.label }}</span>
      </button>
    </div>
    <div
      v-if="showProgress"
      class="service-progress"
      aria-label="当前流程进度"
    >
      <article
        v-for="(step, index) in front.activeServiceProgressSteps"
        :key="step.title"
        :class="{ active: progressActiveIndex === index }"
      >
        <span>{{ index + 1 }}</span>
        <strong>{{ step.title }}</strong>
        <small>{{ step.desc }}</small>
      </article>
    </div>

    <div v-if="showMember" class="member-service-layout">
      <template v-if="!memberContentReady">
        <div class="member-service-loading" role="status">
          <p>正在同步会员身份…</p>
        </div>
      </template>
      <template v-else>
        <div class="membership-sidebar-slot">
          <MembershipSidebar />
        </div>
        <div class="member-service-main">
          <MembershipPathChoicePanel v-if="front.needsMembershipChoice" />
          <div
            v-else
            class="self-service-grid self-service-grid--single"
          >
            <OrganizationMembershipStatusCard v-if="showOrganizationMembership" />
            <MembershipApplicationCard v-else />
            <MembershipPaymentCard v-if="showMembershipPayment" />
          </div>
        </div>
      </template>
    </div>

    <div
      v-else
      class="self-service-grid"
      :class="{
        'self-service-grid--centered': isConferenceCentered,
        'self-service-grid--hall': front.activeServiceTab === 'main'
      }"
    >
      <ServiceHallCard v-if="front.activeServiceTab === 'main'" />
      <BindingCard v-if="showBinding" />
      <ConferenceListCard v-if="showConferenceList" />
      <ConferenceDetailCard v-if="showConferenceDetail" />
      <ConferencePaymentCard v-if="showConferencePayment" />
      <ParticipantInfoCard v-if="showConferenceAttendee" />
      <SocietySciencePanel v-if="front.activeServiceTab === 'science'" />
      <ServiceTopicCard v-else-if="front.serviceTopicContent" />
    </div>
    <ConferenceNoticeModal v-if="showConference" />
  </section>
</template>

<script>
import MembershipPathChoicePanel from './MembershipPathChoicePanel.vue'
import MembershipSidebar from './MembershipSidebar.vue'
import ServiceHallCard from './ServiceHallCard.vue'
import BindingCard from './BindingCard.vue'
import ConferenceListCard from './ConferenceListCard.vue'
import ConferenceDetailCard from './ConferenceDetailCard.vue'
import ConferencePaymentCard from './ConferencePaymentCard.vue'
import ConferenceNoticeModal from './ConferenceNoticeModal.vue'
import MembershipApplicationCard from './MembershipApplicationCard.vue'
import OrganizationMembershipStatusCard from './OrganizationMembershipStatusCard.vue'
import MembershipPaymentCard from './MembershipPaymentCard.vue'
import ParticipantInfoCard from './ParticipantInfoCard.vue'
import ServiceTopicCard from './ServiceTopicCard.vue'
import SocietySciencePanel from './SocietySciencePanel.vue'

const TAB_ICONS = {
  home: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>',
  branches: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 4h6v4H9V4Zm-4 12h5v4H5v-4Zm9 0h5v4h-5v-4ZM12 8v4M7.5 12h9v4" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>',
  member: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="4" y="5" width="16" height="11" rx="1.5" stroke="currentColor" stroke-width="1.7"/><path d="M8 9h5M8 12h3M15 16l2 3 2-3" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  conference: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3.5" y="5" width="17" height="15" rx="2" stroke="currentColor" stroke-width="1.7"/><path d="M3.5 10h17M8 3.5v4M16 3.5v4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><circle cx="12" cy="15" r="1.4" fill="currentColor"/></svg>',
  globe: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.7"/><path d="M4.5 12h15M12 4c2.4 2.6 3.6 5.3 3.6 8s-1.2 5.4-3.6 8c-2.4-2.6-3.6-5.3-3.6-8s1.2-5.4 3.6-8Z" stroke="currentColor" stroke-width="1.7"/></svg>',
  megaphone: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 10v4h3l5 4V6L7 10H4Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="M16 9.5a3.5 3.5 0 0 1 0 5M18.6 7.4a6.6 6.6 0 0 1 0 9.2" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
  medal: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="14" r="5.5" stroke="currentColor" stroke-width="1.7"/><path d="m9 4 3 3 3-3M12 11.2l1.1 2.2 2.4.4-1.7 1.7.4 2.4L12 16.7l-2.2 1.2.4-2.4-1.7-1.7 2.4-.4L12 11.2Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>'
}

export default {
  name: 'SelfServicePanel',
  components: {
    MembershipPathChoicePanel,
    MembershipSidebar,
    ServiceHallCard,
    BindingCard,
    ConferenceListCard,
    ConferenceDetailCard,
    ConferencePaymentCard,
    ConferenceNoticeModal,
    MembershipApplicationCard,
    OrganizationMembershipStatusCard,
    MembershipPaymentCard,
    ParticipantInfoCard,
    ServiceTopicCard,
    SocietySciencePanel
  },
  inject: ['front'],
  methods: {
    tabIcon(name) {
      return TAB_ICONS[name] || TAB_ICONS.home
    }
  },
  computed: {
    showBinding() {
      return this.front.activeServiceTab === 'branches'
    },
    showMember() {
      return this.front.activeServiceTab === 'member'
    },
    /** 身份与服务器同步后即可渲染会员区（不等待申请/缴费列表） */
    memberContentReady() {
      return !this.front.isLoggedIn || this.front.membershipSessionSynced
    },
    showProgress() {
      if (this.front.activeServiceTab === 'main') {
        return false
      }
      // CMS 资讯类 Tab：无真实办理步骤，不展示 123
      if (['international', 'science', 'rewards'].includes(this.front.activeServiceTab)) {
        return false
      }
      // 会员 Tab 等数据就绪后再展示步骤条，与下方内容同帧出现
      if (this.showMember && !this.memberContentReady) {
        return false
      }
      return true
    },
    /** 入会审核通过后（待缴费）、正式会员续费/升级时才展示缴费卡 */
    showMembershipPayment() {
      if (!this.showMember || !this.memberContentReady || this.front.needsMembershipChoice) {
        return false
      }
      if (this.front.canSubmitJoinApplication) {
        return false
      }
      const pendingJoin = (this.front.pendingApplications || [])
        .some(item => item && item.applicationType === 'JOIN')
      if (pendingJoin) {
        return false
      }
      return Boolean(
        this.front.joinApplicationApproved
        || this.front.canAccessMembershipPayment
        || this.front.canPayPermanentFee
        || this.front.canUpgradeToPermanent
      )
    },
    showOrganizationMembership() {
      return Boolean(this.front.organizationBindingStatus) && !this.front.isActiveMember
    },
    showConference() {
      return this.front.activeServiceTab === 'conference'
    },
    showConferenceList() {
      return this.showConference && this.front.conferenceSubView === 'list'
    },
    showConferenceDetail() {
      return this.showConference && this.front.conferenceSubView === 'detail'
    },
    showConferencePayment() {
      return this.showConference && this.front.conferenceSubView === 'payment'
    },
    showConferenceAttendee() {
      return this.showConference && this.front.conferenceSubView === 'attendee'
    },
    isConferenceCentered() {
      return this.showConferenceDetail || this.showConferencePayment || this.showConferenceAttendee
    },
    progressActiveIndex() {
      if (this.showConference) {
        return this.front.conferenceProgressActiveIndex
      }
      return 0
    }
  }
}
</script>
